import { Link, getRouteApi, useNavigate, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  CONDITIONS_KEY,
  SHEET_NAME_MAX,
  SheetEvaluator,
  condition,
  conditionLabel,
  decodeConditions,
  encodeConditions,
  findBuiltIn,
  isBuiltIn,
  javaIsBlank,
  withValue,
  writeSheet,
  type Condition,
  type SheetTemplate,
} from '@realmery/realm-core';
import { realms } from '../../db/client.ts';
import { realmFile, type SheetEntryChoice } from '../../db/protocol.ts';
import { recordRoll } from '../../dice/diceStore.ts';
import { settings } from '../../settings.ts';
import { entryChoiceLabel } from './NewSheet.tsx';
import { SheetForm } from './SheetForm.tsx';
import { message, saveJson } from './SheetsPage.tsx';

/**
 * One character sheet, as the desktop's sheet page: its template laid out by {@link SheetForm},
 * saved a moment after each change, its conditions, the entry it belongs to, and the template's
 * licence at the bottom, as the game's licence requires. Rolls go into the Dice Roller's
 * history, labelled with the character's name.
 */

const route = getRouteApi('/_app/realm/$realmKey/sheets/$sheetId');
const SAVE_DELAY = 600;

/** The conditions to offer: the template's own, or - for an older copy of a built-in - the built-in's. */
export function conditionsFor(template: SheetTemplate): readonly string[] {
  if (template.conditions.length > 0 || !isBuiltIn(template)) return template.conditions;
  return findBuiltIn(template.builtinKey)?.conditions ?? [];
}

export function SheetPage() {
  const view = route.useLoaderData();
  const { realmKey } = route.useParams();
  if (!view) {
    return (
      <article className="document">
        <h1>No such sheet</h1>
        <p>
          It may have been deleted.{' '}
          <Link to="/realm/$realmKey/sheets" params={{ realmKey }}>
            All sheets
          </Link>
        </p>
      </article>
    );
  }
  return <Sheet key={view.sheet.id} />;
}

function Sheet() {
  const view = route.useLoaderData()!;
  const { template } = view;
  const { realmKey } = route.useParams();
  const file = realmFile(realmKey);
  const router = useRouter();
  const navigate = useNavigate();
  const sheetId = view.sheet.id;
  const [values, setValues] = useState<ReadonlyMap<string, string>>(view.sheet.values);
  const [status, setStatus] = useState('Saved');
  const [name, setName] = useState(view.sheet.name);
  const [panel, setPanel] = useState<'none' | 'link' | 'delete' | 'condition'>('none');
  const [error, setError] = useState('');
  const pending = useRef<ReadonlyMap<string, string> | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const sheetName = useRef(view.sheet.name);

  const saveNow = useCallback(async () => {
    window.clearTimeout(timer.current);
    const toSave = pending.current;
    if (!toSave) return;
    pending.current = null;
    try {
      await realms.setSheetValues(file, sheetId, toSave);
      if (!pending.current) setStatus(`Saved ${new Date().toLocaleTimeString()}`);
    } catch (e) {
      setStatus('Not saved');
      setError(message(e, 'The sheet could not be saved.'));
    }
  }, [file, sheetId]);

  // Whatever is still waiting is saved when the page goes.
  useEffect(() => () => void saveNow(), [saveNow]);

  function change(next: ReadonlyMap<string, string>) {
    setValues(next);
    pending.current = next;
    setStatus('Unsaved changes');
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void saveNow(), SAVE_DELAY);
  }

  async function act(work: () => Promise<void>, fallback: string) {
    setError('');
    try {
      await saveNow();
      await work();
    } catch (e) {
      setError(message(e, fallback));
    }
  }

  async function commitName() {
    if (javaIsBlank(name) || name.trim() === sheetName.current) return;
    await act(async () => {
      await realms.renameSheet(file, sheetId, name);
      sheetName.current = name.trim();
    }, 'The sheet could not be renamed.');
  }

  async function remove() {
    await act(async () => {
      window.clearTimeout(timer.current);
      pending.current = null;
      await realms.deleteSheet(file, sheetId);
      await router.invalidate();
      void navigate({ to: '/realm/$realmKey/sheets', params: { realmKey }, replace: true });
    }, 'The sheet could not be deleted.');
  }

  const shown = decodeConditions(values.get(CONDITIONS_KEY));
  const setConditions = (updated: readonly Condition[]) => change(withValue(values, CONDITIONS_KEY, encodeConditions(updated)));
  const summary = new SheetEvaluator(template, values).summary();

  return (
    <article className="document sheet-page">
      <p className="eyebrow">
        <Link to="/realm/$realmKey/sheets" params={{ realmKey }}>
          ← All sheets
        </Link>
      </p>
      <label className="sheet-name">
        <span className="visually-hidden">The sheet's name</span>
        <input
          value={name}
          maxLength={SHEET_NAME_MAX}
          title="The sheet's name"
          onChange={(e) => setName(e.currentTarget.value)}
          onBlur={() => void commitName()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void commitName();
          }}
        />
      </label>
      <p className="meta sheet-info">
        <span>
          {template.name}
          {template.system ? ` · ${template.system}` : ''}
        </span>
        <span>
          {view.sheet.entryId && view.entryTitle ? (
            <>
              Belongs to{' '}
              <Link to="/realm/$realmKey/entry/$entryId" params={{ realmKey, entryId: view.sheet.entryId }} search={{}}>
                ↳ {view.entryTitle}
              </Link>{' '}
              <button
                type="button"
                className="quiet"
                title="Keep the sheet, but not as this entry's"
                onClick={() =>
                  void act(async () => {
                    await realms.linkSheet(file, sheetId, null);
                    await router.invalidate();
                  }, 'The sheet could not be unlinked.')
                }
              >
                Unlink
              </button>
            </>
          ) : (
            <>
              Not linked to an entry.{' '}
              <button type="button" className="quiet" title="Make this the sheet of a Person or Creature entry" onClick={() => setPanel('link')}>
                Link to entry…
              </button>
            </>
          )}
        </span>
        <span className={status === 'Unsaved changes' ? 'autosave dirty' : 'autosave'} role="status">
          {status}
        </span>
      </p>
      <div className="actions">
        <button
          type="button"
          title="Print the sheet, laid out for paper, or save it as a PDF"
          onClick={() => void act(async () => void navigate({ to: '/realm/$realmKey/sheets/$sheetId/print', params: { realmKey, sheetId } }), 'The sheet could not be saved.')}
        >
          Print or PDF…
        </button>
        <button type="button" title="Save this sheet, with its template, as a JSON file to share or import" onClick={() => void act(async () => saveJson(writeSheet({ ...view.sheet, name: sheetName.current, values }, template), sheetName.current), 'The sheet could not be exported.')}>
          Export JSON…
        </button>
        <button type="button" className="quiet" title="Delete this sheet (its entry stays)" onClick={() => (settings()['general.confirmDeletes'] ? setPanel('delete') : void remove())}>
          Delete…
        </button>
      </div>
      {panel === 'delete' && (
        <div className="panel confirm" role="alertdialog" aria-label={`Delete ${sheetName.current}`}>
          <p>Delete “{sheetName.current}”'s sheet? Its entry stays.</p>
          <div className="actions">
            <button type="button" className="danger" onClick={() => void remove()}>
              Delete sheet
            </button>
            <button type="button" onClick={() => setPanel('none')}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {panel === 'link' && (
        <LinkEntry
          file={file}
          sheetName={sheetName.current}
          onCancel={() => setPanel('none')}
          onLink={(entryId) =>
            act(async () => {
              await realms.linkSheet(file, sheetId, entryId);
              setPanel('none');
              await router.invalidate();
            }, 'The sheet could not be linked.')
          }
        />
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {summary && <p className="sheet-summary">{summary}</p>}
      <div className="sheet-conditions" role="group" aria-label="Conditions">
        <span className="sheet-field-label">Conditions:</span>
        {shown.length === 0 && <span className="meta">none</span>}
        {shown.map((c, i) => (
          <button key={`${c.name}-${i}`} type="button" className="chip gm-condition" title="Remove this condition" aria-label={`Remove ${conditionLabel(c)}`} onClick={() => setConditions(shown.filter((_, j) => j !== i))}>
            {conditionLabel(c)} ✕
          </button>
        ))}
        <AddCondition presets={conditionsFor(template)} onAdd={(added) => setConditions(added.preset ? [...shown.filter((c) => c.name.toLowerCase() !== added.conditions[0]!.name.toLowerCase()), ...added.conditions] : [...shown, ...added.conditions])} />
      </div>
      <SheetForm
        template={template}
        values={values}
        onChange={(key, value) => change(withValue(values, key, value))}
        onRolled={(field, result) => recordRoll(`${sheetName.current}: ${field.label}`, result)}
      />
      {template.license && (
        <p className="sheet-license">
          {template.name} · {template.license}
        </p>
      )}
    </article>
  );
}

function AddCondition({ presets, onAdd }: { presets: readonly string[]; onAdd: (added: { conditions: Condition[]; preset: boolean }) => void }) {
  const [other, setOther] = useState<string | null>(null);
  const id = useId();
  return (
    <>
      <label htmlFor={id} className="visually-hidden">
        Add a condition
      </label>
      <select
        id={id}
        value=""
        title="Add a condition"
        onChange={(e) => {
          const value = e.currentTarget.value;
          if (value === '\u0000other') setOther('');
          else if (value) onAdd({ conditions: [condition(value, 0)], preset: true });
        }}
      >
        <option value="">+ Add…</option>
        {presets.map((preset) => (
          <option key={preset} value={preset}>
            {preset}
          </option>
        ))}
        <option value={'\u0000other'}>Other…</option>
      </select>
      {other !== null && (
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!javaIsBlank(other)) onAdd({ conditions: decodeConditions(other), preset: false });
            setOther(null);
          }}
        >
          <label>
            <span className="visually-hidden">A condition for this character</span>
            <input autoFocus value={other} placeholder="Blessed:3" onChange={(e) => setOther(e.currentTarget.value)} />
          </label>
          <button type="submit">Add</button>
          <button type="button" className="quiet" onClick={() => setOther(null)}>
            Cancel
          </button>
        </form>
      )}
    </>
  );
}

/** Link to entry: any entry without a sheet, people and creatures first; one titled as the sheet is chosen. */
export function LinkEntry({ file, sheetName, onLink, onCancel }: { file: string; sheetName: string; onLink: (entryId: string) => Promise<void>; onCancel: () => void }) {
  const [entries, setEntries] = useState<SheetEntryChoice[] | null>(null);
  const [chosen, setChosen] = useState('');
  const id = useId();
  useEffect(() => {
    let live = true;
    void realms.sheetEntryChoices(file).then((found) => {
      if (!live) return;
      setEntries(found);
      setChosen(found.find((e) => e.title.toLowerCase() === sheetName.toLowerCase())?.id ?? '');
    });
    return () => {
      live = false;
    };
  }, [file, sheetName]);
  return (
    <form
      className="panel"
      aria-label="Link to entry"
      onSubmit={(e) => {
        e.preventDefault();
        if (chosen) void onLink(chosen);
      }}
    >
      <label htmlFor={id}>Which lore entry is “{sheetName}”?</label>
      <p className="meta">People and creatures are listed first.</p>
      <select id={id} value={chosen} onChange={(e) => setChosen(e.currentTarget.value)} disabled={entries === null}>
        <option value="">(Choose an entry)</option>
        {(entries ?? []).map((entry) => (
          <option key={entry.id} value={entry.id}>
            {entryChoiceLabel(entry)}
          </option>
        ))}
      </select>
      <div className="actions">
        <button type="submit" className="primary" disabled={!chosen}>
          Link
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
